import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import serverClient from '../../api/ServerClient';
import { DeleteToken } from '../../auth/Auth';
import Alert from '../Alert';
import feather from 'feather-icons';
import { useNavigate } from 'react-router-dom';
import { formatAmountInput, getUserTimezone, bengaliToEnglishNumber } from '../../util/helper';
import { useLanguage } from '../../context/LanguageContext';
import { SERVER_BASE_URL } from '../../config/app.config';
import { FiCamera, FiTrash2, FiEye, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import AttachmentFilePreview from '../Attachments/AttachmentFilePreview';
import {
    ATTACHMENT_ACCEPT,
    isPreviewableImage,
    validateAttachmentFile,
} from '../../util/attachmentHelper';
import '../../styles/AccountToggleOptions.css';

const TransactionForm = ({
    transaction,
    accounts,
    categories,
    onSuccess,
    onAttachmentAction,
    setLoading,
    onTransactionDelete,
}) => {
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    // Edit mode: existing saved attachment filename, uploads happen immediately
    const [attachment, setAttachment] = useState(transaction?.attachment || null);
    const [imgError, setImgError] = useState(false);
    const [showDeleteAttachmentConfirm, setShowDeleteAttachmentConfirm] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [attachmentBusy, setAttachmentBusy] = useState(false);
    const [attachmentNoticeKey, setAttachmentNoticeKey] = useState('');
    const [attachmentNoticeLeaving, setAttachmentNoticeLeaving] = useState(false);

    const [form, setForm] = useState({
        amount: transaction?.amount || '',
        account_id: transaction?.account_id || '',
        to_account_id: transaction?.to_account_id || null,
        category_id: transaction?.category_id || null,
        transaction_time: transaction?.transaction_time?.slice(0, 16) || '',
        note: transaction?.note || '',
        payee: transaction?.lending_to || '',
    });

    useEffect(() => {
        setForm({
            amount: transaction?.amount || '',
            account_id: transaction?.account_id === 0 ? 0 : transaction?.account_id || '',
            to_account_id:
                transaction?.to_account_id === 0 ? 0 : transaction?.to_account_id || null,
            category_id: transaction?.category_id || null,
            transaction_time: transaction?.transaction_time?.slice(0, 16) || '',
            note: transaction?.note || '',
            payee: transaction?.lending_to || '',
        });
        setAttachment(transaction?.attachment || null);
        setImgError(false);
        setShowDeleteAttachmentConfirm(false);
        setAttachmentBusy(false);
        setAttachmentNoticeKey('');
        setAttachmentNoticeLeaving(false);
    }, [transaction]);

    useEffect(() => {
        if (!attachmentNoticeKey) return undefined;
        setAttachmentNoticeLeaving(false);
        const leaveTimer = setTimeout(() => setAttachmentNoticeLeaving(true), 1300);
        const clearTimer = setTimeout(() => {
            setAttachmentNoticeKey('');
            setAttachmentNoticeLeaving(false);
        }, 1750);
        return () => {
            clearTimeout(leaveTimer);
            clearTimeout(clearTimer);
        };
    }, [attachmentNoticeKey]);

    useEffect(() => {
        feather.replace();
    }, []);

    const attachmentUrl = attachment
        ? `${SERVER_BASE_URL}/images/transaction_attachments/${attachment}`
        : null;

    const handleAttachmentFileChange = async (e) => {
        const file = e.target.files[0];
        e.target.value = null;
        if (attachmentBusy) return;
        if (!file) return;
        const errorMessage = validateAttachmentFile(file, t);
        if (errorMessage) {
            setError(errorMessage);
            return;
        }

        setError('');
        setAttachmentBusy(true);
        const formData = new FormData();
        formData.append('attachment', file);
        try {
            const res = await serverClient.post(
                `/api/user/transaction/${transaction.id}/attachment`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            setAttachment(res.data.attachment);
            setImgError(false);
            setAttachmentNoticeKey('attachmentUploaded');
            onAttachmentAction?.();
        } catch (err) {
            if (err.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
            } else {
                setError(err.response?.data?.message || t('somethingWentWrong'));
            }
        } finally {
            setAttachmentBusy(false);
        }
    };

    const requestAttachmentDelete = () => {
        setError('');
        setShowDeleteAttachmentConfirm(true);
    };
    const cancelAttachmentDelete = () => setShowDeleteAttachmentConfirm(false);

    const confirmAttachmentDelete = async () => {
        setAttachmentBusy(true);
        setError('');
        try {
            await serverClient.delete(`/api/user/transaction/${transaction.id}/attachment`);
            setAttachment(null);
            setShowDeleteAttachmentConfirm(false);
            setAttachmentNoticeKey('attachmentDeleted');
            onAttachmentAction?.();
        } catch (err) {
            if (err.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
            } else {
                setError(err.response?.data?.message || t('somethingWentWrong'));
            }
        } finally {
            setAttachmentBusy(false);
        }
    };

    let accountOptions;

    if (transaction?.transaction_type === 'transfer') {
        accountOptions = [
            { value: 0, label: 'Out Of Wallet' },
            ...(accounts?.map((account) => ({ value: account.id, label: account.name })) || []),
        ];
    } else {
        accountOptions = accounts.map((acc) => ({ value: acc.id, label: acc.name }));
    }
    const categoryOptions = categories
        .filter((cat) =>
            transaction?.transaction_type === 'expense'
                ? cat.type === 'Expense'
                : transaction?.transaction_type === 'income'
                  ? cat.type === 'Income'
                  : true
        )
        .map((cat) => ({ value: cat.id, label: cat.name }));

    const handleSubmit = async (e) => {
        e.preventDefault();

        const cleanedAmount = parseFloat(form.amount);

        if (isNaN(cleanedAmount) || cleanedAmount === 0) {
            setError(t('amountCannotBeEmpty'));
            return;
        }

        if (form.account_id === '' || form.account_id === null || form.account_id === undefined) {
            setError(t('selectAccount'));
            return;
        }
        if (
            (transaction?.transaction_type === 'transfer' ||
                transaction?.transaction_type === 'withdraw') &&
            (form.to_account_id === null ||
                form.to_account_id === undefined ||
                form.to_account_id === '')
        ) {
            setError(t('selectToAccount'));
            return;
        }

        if (form.account_id === form.to_account_id) {
            setError(t('accountsCannotBeSame'));
            return;
        }

        if (
            (transaction?.transaction_type === 'expense' ||
                transaction?.transaction_type === 'income') &&
            !form.category_id
        ) {
            setError(t('selectCategory'));
            return;
        }

        if (!form.transaction_time) {
            setError(t('chooseDateAndTime'));
            return;
        }

        const payload = {
            amount: cleanedAmount,
            account_id: form.account_id,
            category_id: form.category_id,
            transaction_type:
                transaction.transaction_type === 'withdraw'
                    ? 'transfer'
                    : transaction.transaction_type,
            is_lending: !!form.payee,
            lending_to: form.payee || null,
            transaction_time: form.transaction_time,
            note: form.note,
            to_account_id: form.to_account_id,
            timezone: getUserTimezone(),
        };

        try {
            setLoading(true);
            const res = await serverClient.put(`/api/user/transaction/${transaction.id}`, payload);
            if (res.status === 200 || res.status === 201) onSuccess();
        } catch (e) {
            if (e.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
                navigate('/login');
            } else {
                setError(e.response?.data?.message || t('somethingWentWrong'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            {error && <Alert alert="danger">{error}</Alert>}

            <div className="form-group">
                <label className="form-label">{t('amount')}</label>
                <input
                    required
                    type="text"
                    inputMode="decimal"
                    className="form-control mb-3"
                    placeholder={t('enterAmount')}
                    value={(() => {
                        if (
                            form.amount === '' ||
                            form.amount === null ||
                            form.amount === undefined
                        ) {
                            return '';
                        }
                        // Get the raw number as string
                        const rawValue = String(form.amount).replace(/^-/, '');
                        return formatAmountInput(rawValue, language);
                    })()}
                    onChange={(e) => {
                        const value = e.target.value;

                        // Convert Bengali to English and remove commas
                        const cleanedValue = bengaliToEnglishNumber(value)
                            .replace(/,/g, '')
                            .replace(/[^\d.]/g, '');

                        if (cleanedValue === '') {
                            setForm({ ...form, amount: '' });
                            return;
                        }

                        // Prevent multiple decimal points
                        const parts = cleanedValue.split('.');
                        if (parts.length > 2) {
                            return;
                        }

                        // Limit integer part to 9 digits
                        if (parts[0].length > 9) {
                            return;
                        }

                        // For expense, always store as negative
                        if (transaction?.transaction_type === 'expense') {
                            setForm({ ...form, amount: `-${cleanedValue}` });
                        } else {
                            setForm({ ...form, amount: cleanedValue });
                        }
                    }}
                />
            </div>

            {/* Account */}
            <div className="form-group">
                <label className="form-label">
                    {transaction?.transaction_type === 'transfer' ||
                    transaction?.transaction_type === 'withdraw'
                        ? t('fromAccount')
                        : t('account')}
                </label>
                <Select
                    options={accountOptions}
                    classNamePrefix="react-select"
                    value={accountOptions.find((opt) => opt.value === Number(form.account_id))}
                    onChange={(option) => setForm({ ...form, account_id: option.value })}
                />
            </div>

            {/* To Account */}
            {(transaction?.transaction_type === 'transfer' ||
                transaction?.transaction_type === 'withdraw') && (
                <div className="form-group">
                    <label className="form-label">{t('toAccount')}</label>
                    <Select
                        options={accountOptions}
                        classNamePrefix="react-select"
                        value={accountOptions.find(
                            (opt) => opt.value === Number(form.to_account_id)
                        )}
                        onChange={(option) => setForm({ ...form, to_account_id: option.value })}
                    />
                </div>
            )}

            {/* Category */}
            {(transaction?.transaction_type === 'expense' ||
                transaction?.transaction_type === 'income') && (
                <div className="form-group">
                    <label className="form-label">{t('category')}</label>
                    <Select
                        options={categoryOptions}
                        classNamePrefix="react-select"
                        value={categoryOptions.find((opt) => opt.value === form.category_id)}
                        onChange={(option) => setForm({ ...form, category_id: option.value })}
                    />
                </div>
            )}

            {/* Payee */}
            {[20, 21].includes(form.category_id) && (
                <div className="form-group">
                    <label className="form-label">
                        {form.category_id === 20 ? t('payee') : t('borrowedFrom')}
                    </label>
                    <input
                        type="text"
                        className="form-control mb-3"
                        placeholder={
                            form.category_id === 20 ? t('payeeName') : t('borrowedFromName')
                        }
                        value={form.payee}
                        onChange={(e) => setForm({ ...form, payee: e.target.value })}
                    />
                </div>
            )}

            {/* Date Time */}
            <div className="form-group">
                <label className="form-label">{t('dateTime')}</label>
                <input
                    type="datetime-local"
                    className="form-control mb-3"
                    value={form.transaction_time}
                    onChange={(e) => setForm({ ...form, transaction_time: e.target.value })}
                />
            </div>

            {/* Remarks */}
            <div className="form-group">
                <label className="form-label">{t('remarks')}</label>
                <input
                    type="text"
                    className="form-control mb-3"
                    placeholder={t('noteOrRemarks')}
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                />
            </div>

            {/* Attachment – edit mode: upload/replace/delete immediately */}
            <div className="form-group">
                <label className="form-label">{t('transactionAttachment')}</label>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ATTACHMENT_ACCEPT}
                    style={{ display: 'none' }}
                    disabled={attachmentBusy}
                    onChange={handleAttachmentFileChange}
                />
                {attachmentUrl ? (
                    <div className="account-attachment-preview">
                        <div className="attachment-thumb-wrapper">
                            {attachmentBusy && (
                                <div
                                    className="attachment-action-overlay"
                                    role="status"
                                    aria-live="polite"
                                >
                                    <span
                                        className="spinner-border spinner-border-sm"
                                        aria-hidden="true"
                                    ></span>
                                    <span>{t('uploadingAttachment')}</span>
                                </div>
                            )}
                            {imgError ? (
                                <div className="attachment-broken">
                                    <FiAlertCircle size={32} className="broken-icon" />
                                    <span className="broken-text">{t('imageUnavailable')}</span>
                                    <button
                                        type="button"
                                        className="broken-replace-btn"
                                        disabled={attachmentBusy}
                                        onClick={() => {
                                            setImgError(false);
                                            fileInputRef.current?.click();
                                        }}
                                    >
                                        <FiCamera size={13} /> {t('changeAttachment')}
                                    </button>
                                </div>
                            ) : (
                                <AttachmentFilePreview
                                    url={attachmentUrl}
                                    filename={attachment}
                                    imgError={imgError}
                                    onImgError={() => setImgError(true)}
                                    onClick={() => {
                                        if (isPreviewableImage(attachment)) setLightboxOpen(true);
                                        else {
                                            window.open(
                                                attachmentUrl,
                                                '_blank',
                                                'noopener,noreferrer'
                                            );
                                        }
                                    }}
                                    className="attachment-thumb"
                                    alt="Transaction attachment"
                                />
                            )}
                        </div>
                        {!imgError && (
                            <div className="attachment-actions">
                                {isPreviewableImage(attachment) && (
                                    <button
                                        type="button"
                                        className="btn btn-sm attachment-btn-view"
                                        disabled={attachmentBusy}
                                        onClick={() => setLightboxOpen(true)}
                                    >
                                        <FiEye size={13} /> {t('viewAttachment')}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="btn btn-sm attachment-btn-change"
                                    disabled={attachmentBusy}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <FiCamera size={13} /> {t('changeAttachment')}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm attachment-btn-delete"
                                    disabled={attachmentBusy}
                                    onClick={requestAttachmentDelete}
                                >
                                    <FiTrash2 size={13} />
                                </button>
                            </div>
                        )}
                        {showDeleteAttachmentConfirm && (
                            <div
                                className="attachment-delete-confirm"
                                role="alertdialog"
                                aria-live="polite"
                            >
                                <p className="delete-confirm-text">
                                    {t('confirmAttachmentDelete')}
                                </p>
                                <div className="delete-confirm-actions">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={cancelAttachmentDelete}
                                    >
                                        {t('cancel')}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        disabled={attachmentBusy}
                                        onClick={confirmAttachmentDelete}
                                    >
                                        {t('deleteAttachment')}
                                    </button>
                                </div>
                            </div>
                        )}
                        {attachmentBusy && (
                            <div
                                className="attachment-inline-status attachment-inline-status-loading"
                                role="status"
                                aria-live="polite"
                            >
                                <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                                <span>{t('uploadingAttachment')}</span>
                            </div>
                        )}
                        {attachmentNoticeKey && !attachmentBusy && (
                            <div
                                className={`attachment-inline-status attachment-inline-status-success ${attachmentNoticeLeaving ? 'is-leaving' : ''}`}
                                role="status"
                                aria-live="polite"
                            >
                                <FiCheckCircle size={14} />
                                <span>{t(attachmentNoticeKey)}</span>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <button
                            type="button"
                            className="attachment-upload-placeholder"
                            disabled={attachmentBusy}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <FiCamera size={22} className="attach-icon" />
                            <span className="attach-label">{t('uploadAttachment')}</span>
                            <span className="attach-hint">
                                JPEG · PNG · WebP · PDF · TXT · max 4 MB
                            </span>
                        </button>
                        {attachmentBusy && (
                            <div
                                className="attachment-inline-status attachment-inline-status-loading"
                                role="status"
                                aria-live="polite"
                            >
                                <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                                <span>{t('uploadingAttachment')}</span>
                            </div>
                        )}
                    </>
                )}
            </div>

            {lightboxOpen && attachmentUrl && (
                <div className="account-attachment-lightbox" onClick={() => setLightboxOpen(false)}>
                    <img src={attachmentUrl} alt="Attachment" className="lightbox-img" />
                    <button
                        type="button"
                        className="lightbox-close"
                        aria-label={t('close')}
                        onClick={(e) => {
                            e.stopPropagation();
                            setLightboxOpen(false);
                        }}
                    >
                        &times;
                    </button>
                </div>
            )}

            <div className="fixed-footer">
                <button type="submit" className="btn theme-btn successfully w-100">
                    <i className="icon button-icon" data-feather="edit"></i>
                    {t('update')}
                </button>

                <button
                    type="button"
                    className="btn btn-danger successfully w-100"
                    onClick={() => onTransactionDelete(transaction?.id)}
                    data-bs-toggle="modal"
                    data-bs-target="#deleteTransaction"
                >
                    <i className="icon button-icon" data-feather="trash-2"></i>
                    {t('delete')}
                </button>
            </div>
        </form>
    );
};

export default TransactionForm;
